import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CheckCircle, Phone } from 'lucide-react';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import ProductCTAButtons from '../components/ProductCTAButtons';
import BrandChip from '../components/BrandChip';
import { ProductReviews } from '../components/product/ProductReviews';
import NotFoundPage from './NotFoundPage';

const API = process.env.REACT_APP_BACKEND_URL;

const FAMILY_STYLE = {
  SMARTBOT: { badge: 'bg-emerald-500/15 text-emerald-300', accent: 'emerald' },
  POOLBOT: { badge: 'bg-blue-500/15 text-blue-300', accent: 'blue' },
  SHINEBOT: { badge: 'bg-cyan-500/15 text-cyan-300', accent: 'cyan' },
  LAWNBOT: { badge: 'bg-green-500/15 text-green-300', accent: 'emerald' },
};

const FAMILY_MAP = [
  ['Floor', 'SMARTBOT'],
  ['Pool', 'POOLBOT'],
  ['Window', 'SHINEBOT'],
  ['Lawn', 'LAWNBOT'],
];
const familyFor = (category = '') => {
  const hit = FAMILY_MAP.find(([k]) => category.includes(k));
  return hit ? hit[1] : null;
};

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

export default function GenericProductPage({ slug }) {
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    fetch(`${API}/api/products/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setProduct)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <NotFoundPage />;

  if (!product) {
    return (
      <>
        <Header />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center" data-testid="generic-product-loading">
          <p className="text-gray-400">Loading product…</p>
        </main>
        <Footer />
      </>
    );
  }

  const price = product.salePrice || product.originalPrice;

  return (
    <>
      <Helmet>
        <title>{`${product.name} — Price ${formatPrice(price)}, Specs & Buy Online | Harpalyk`}</title>
        <meta name="description" content={product.description || `${product.name} by Harpalyk — specs, price and buying options.`} />
        <link rel="canonical" href={`https://harpalyk.com/products/${product.slug}`} />
      </Helmet>

      <Header />

      <main className="pt-20" data-testid={`${product.slug}-page`}>
        <Breadcrumbs items={[{ label: 'Products', href: '/#products' }, { label: product.name }]} />

        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 px-6 lg:px-12">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-heading text-sm font-bold tracking-[0.3em] text-white/50 uppercase mb-3">Harpalyk</p>
              <span className={`inline-block ${(FAMILY_STYLE[familyFor(product.category)] || {}).badge || 'bg-white/10 text-emerald-300'} px-4 py-2 rounded-full text-sm font-medium mb-6`}>
                {familyFor(product.category) ? `${familyFor(product.category)} · ${product.category}` : product.category}
              </span>
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-4" data-testid="generic-product-name">
                {product.name}
              </h1>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">{product.description}</p>
              <p className="flex items-baseline gap-3 mb-8">
                {product.onSale && product.originalPrice !== product.salePrice && (
                  <span className="text-lg text-gray-400 line-through">{formatPrice(product.originalPrice)}</span>
                )}
                <span className="text-4xl font-bold text-white" data-testid="generic-product-price">{formatPrice(price)}</span>
                <span className="text-sm text-emerald-300">incl. GST</span>
              </p>
              <a
                href="tel:+917011370322"
                className="inline-flex items-center gap-2 border border-white/25 hover:bg-white hover:text-gray-900 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                data-testid="generic-product-call"
              >
                <Phone size={16} /> Talk to a Product Expert
              </a>
            </div>
            <div className="relative w-full max-w-md mx-auto">
              <img
                src={product.image}
                alt={`Harpalyk ${familyFor(product.category) ? familyFor(product.category) + ' ' : ''}${product.name.replace(/^Harpalyk\s+/i, '')} — ${product.category}`}
                className="w-full rounded-2xl shadow-2xl bg-white/5 object-contain"
                data-testid="generic-product-image"
              />
              <BrandChip family={familyFor(product.category)} />
            </div>
          </div>
        </section>

        <ProductCTAButtons slug={product.slug} name={product.name} />

        {product.features?.length > 0 && (
          <section className="py-16 px-6 lg:px-12 bg-gray-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
              <ul className="grid md:grid-cols-2 gap-4" data-testid="generic-product-features">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 p-4">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <ProductReviews product={{ slug: product.slug, name: product.name, accent: (FAMILY_STYLE[familyFor(product.category)] || {}).accent || 'emerald' }} />

        <ProductCTAButtons slug={product.slug} name={product.name} />
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
}
